import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Briefcase } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface JobPosition {
  id: string;
  title: string;
  description: string | null;
  is_active: boolean;
  sort_order: number | null;
}

export const JobPositionsSettings = () => {
  const [positions, setPositions] = useState<JobPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [positionToDelete, setPositionToDelete] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");

  useEffect(() => {
    fetchPositions();
  }, []);

  const fetchPositions = async () => {
    try {
      const { data, error } = await supabase
        .from('job_positions')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setPositions(data || []);
    } catch (error) {
      console.error('Error fetching job positions:', error);
      toast.error("Fehler beim Laden der Stellen");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newTitle.trim()) {
      toast.error("Bitte geben Sie einen Stellentitel ein");
      return;
    }

    try {
      const maxOrder = positions.length > 0 
        ? Math.max(...positions.map(p => p.sort_order || 0)) 
        : 0;

      const { error } = await supabase
        .from('job_positions')
        .insert({
          title: newTitle.trim(),
          description: newDescription.trim() || null,
          sort_order: maxOrder + 1
        });

      if (error) throw error;

      toast.success("Stelle erfolgreich hinzugefügt");
      setNewTitle("");
      setNewDescription("");
      fetchPositions();
    } catch (error) {
      console.error('Error adding job position:', error);
      toast.error("Fehler beim Hinzufügen der Stelle");
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('job_positions')
        .update({ is_active: isActive })
        .eq('id', id);

      if (error) throw error;

      setPositions(positions.map(p => 
        p.id === id ? { ...p, is_active: isActive } : p
      ));
      toast.success(isActive ? "Stelle aktiviert" : "Stelle deaktiviert");
    } catch (error) {
      console.error('Error updating job position:', error);
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const startEdit = (position: JobPosition) => {
    setEditingId(position.id);
    setEditTitle(position.title);
    setEditDescription(position.description || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
    setEditDescription("");
  };

  const saveEdit = async () => {
    if (!editingId || !editTitle.trim()) return;

    try {
      const { error } = await supabase
        .from('job_positions')
        .update({
          title: editTitle.trim(),
          description: editDescription.trim() || null
        })
        .eq('id', editingId);

      if (error) throw error;

      toast.success("Stelle aktualisiert");
      cancelEdit();
      fetchPositions();
    } catch (error) {
      console.error('Error updating job position:', error);
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleDelete = (id: string) => {
    setPositionToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!positionToDelete) return;

    try {
      const { error } = await supabase
        .from('job_positions')
        .delete()
        .eq('id', positionToDelete);

      if (error) throw error;

      toast.success("Stelle gelöscht");
      fetchPositions();
    } catch (error) {
      console.error('Error deleting job position:', error);
      toast.error("Fehler beim Löschen");
    } finally {
      setDeleteDialogOpen(false);
      setPositionToDelete(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">Laden...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Stellenverwaltung
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Verwalten Sie die Stellen, für die sich Bewerber bewerben können
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Add new position */}
          <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium">Neue Stelle hinzufügen</h4>
            <div className="grid gap-4">
              <div>
                <label className="text-sm font-medium">Stellentitel *</label>
                <Input
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="z.B. Bauleiter, Elektriker, Projektmanager..."
                />
              </div>
              <div>
                <label className="text-sm font-medium">Beschreibung (optional)</label>
                <Textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="Kurze Beschreibung der Stelle..."
                  rows={2}
                />
              </div>
              <Button onClick={handleAdd} className="w-fit gap-2">
                <Plus className="h-4 w-4" />
                Stelle hinzufügen
              </Button>
            </div>
          </div>

          {/* Positions list */}
          <div className="space-y-3">
            <h4 className="font-medium">Vorhandene Stellen ({positions.length})</h4>
            {positions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
                Keine Stellen vorhanden. Fügen Sie Stellen hinzu, die im Bewerbungsportal angezeigt werden.
              </div>
            ) : (
              positions.map((position) => (
                <Card key={position.id} className={!position.is_active ? "opacity-60" : ""}>
                  <CardContent className="pt-4">
                    {editingId === position.id ? (
                      <div className="space-y-3">
                        <Input
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          placeholder="Stellentitel"
                        />
                        <Textarea
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          placeholder="Beschreibung (optional)"
                          rows={2}
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={saveEdit}>Speichern</Button>
                          <Button size="sm" variant="outline" onClick={cancelEdit}>Abbrechen</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1">
                          <GripVertical className="h-4 w-4 text-muted-foreground" />
                          <div className="flex-1">
                            <h3 className="font-medium">{position.title}</h3>
                            {position.description && (
                              <p className="text-sm text-muted-foreground">{position.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">Aktiv</span>
                            <Switch
                              checked={position.is_active}
                              onCheckedChange={(checked) => handleToggleActive(position.id, checked)}
                            />
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => startEdit(position)}
                          >
                            Bearbeiten
                          </Button>
                          <Button
                            variant="destructive"
                            size="icon"
                            onClick={() => handleDelete(position.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Stelle löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie diese Stelle löschen möchten? 
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default JobPositionsSettings;
